import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Button,
  FormField,
  Icon,
  Stack,
  P,
  H4,
} from '@fashionnovateam/nova-ui-toolkit';
import { ControlledFormInput } from '../../../../Lib/Utils/FormUtils/ControlledFormInput';
import { AddTagFormData, FabricSection } from '../types';
import { FiberContentDataGrid } from '../components/FiberContentDataGrid';

type FabricSectionKey =
  | 'contrastSection'
  | 'liningSection'
  | 'fillingSection'
  | 'coatingSection'
  | 'backingSection';

export function AdditionalFabricsSection() {
  const { control, watch, setValue } = useFormContext<AddTagFormData>();

  const contrastSection = watch('contrastSection');
  const liningSection = watch('liningSection');
  const fillingSection = watch('fillingSection');
  const coatingSection = watch('coatingSection');
  const backingSection = watch('backingSection');

  const fabricTypes: Array<{
    key: FabricSectionKey;
    label: string;
    title: string;
    section: FabricSection | undefined;
  }> = [
    {
      key: 'contrastSection',
      label: 'Add Contrast',
      title: 'Contrast',
      section: contrastSection,
    },
    {
      key: 'liningSection',
      label: 'Add Lining',
      title: 'Lining',
      section: liningSection,
    },
    {
      key: 'fillingSection',
      label: 'Add Filling',
      title: 'Filling',
      section: fillingSection,
    },
    {
      key: 'coatingSection',
      label: 'Add Coating',
      title: 'Coating',
      section: coatingSection,
    },
    {
      key: 'backingSection',
      label: 'Add Backing',
      title: 'Backing',
      section: backingSection,
    },
  ];

  const addFabricSection = (sectionType: FabricSectionKey) => {
    const sectionTypeMap: Record<FabricSectionKey, string> = {
      contrastSection: 'contrast',
      liningSection: 'lining',
      fillingSection: 'filling',
      coatingSection: 'coating',
      backingSection: 'backing',
    };

    const newSection: FabricSection = {
      id: sectionTypeMap[sectionType],
      fiberContents: [{ id: 'fiber-0', fiber: '', percent_of_fibers: 0 }],
      fabricWeight: '',
      fabricCodeName: '',
    };
    setValue(sectionType, newSection);
  };

  const removeFabricSection = (sectionType: FabricSectionKey) => {
    setValue(sectionType, undefined);
  };

  const renderFabricSection = (
    section: FabricSection,
    sectionType: FabricSectionKey,
    sectionTitle: string,
  ) => (
    <div className="rounded-xl border border-neutral-200 shadow-sm p-5 space-y-4">
      <Stack
        direction="row"
        align="center"
        width="full"
        className="justify-between"
      >
        <H4 className="text-lg font-medium text-gray-900">{sectionTitle}</H4>
        <Button
          color="secondary"
          size="sm"
          onClick={() => removeFabricSection(sectionType)}
          className="p-1"
        >
          <Icon name="delete/delete-dustbin-02" size="16px" />
        </Button>
      </Stack>

      <FormField label={`${sectionTitle} Content`} required>
        <P color="disabled" size="sm" className="mb-3">
          Total of all contents must equal 100%.
        </P>

        <FiberContentDataGrid fieldPath={`${sectionType}.fiberContents`} />
      </FormField>

      <ControlledFormInput
        name={`${sectionType}.fabricWeight`}
        label={`${sectionTitle} Fabric Weight`}
        control={control}
        inputType="number"
        inputProps={{
          className: 'w-full',
          placeholder: '0',
          min: '0',
          endAdornment: <span className="text-gray-500 text-sm">gsm</span>,
        }}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      {fabricTypes.map((fabricType) => (
        <div
          key={fabricType.key}
          className="rounded-xl border border-neutral-200 shadow-sm p-5 space-y-4"
        >
          {!fabricType.section ? (
            <Button
              onClick={() => addFabricSection(fabricType.key)}
              color="secondary"
              className="flex items-center gap-2"
            >
              {fabricType.label}
            </Button>
          ) : (
            renderFabricSection(
              fabricType.section,
              fabricType.key,
              fabricType.title,
            )
          )}
        </div>
      ))}
    </div>
  );
}
